import { useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ApplicationContext } from '../../store/context';
import { IApplication } from '../../store/state';
import { OverflowGroup } from './OverflowGroup';

export interface ITabLayout {
  visibleTabs: IApplication[];
  overflowTabs: IApplication[];
}

export function useOverflowGroup(
  openedApplicationIds: Set<string>,
  applicationID: string | null,
  maxNumberOfVisibleTabs: number,
) {
  const [tabLayout, setTabLayout] = useState(
    () => new OverflowGroup(maxNumberOfVisibleTabs),
  );

  // FIXME: This logic shouldn't be on a useEffect

  useEffect(() => {
    setTabLayout(() =>
      Array.from(openedApplicationIds).reduce((acc, appId) => {
        const next = acc.clone();
        next.add(appId);
        return next;
      }, new OverflowGroup(maxNumberOfVisibleTabs)),
    );
  }, [openedApplicationIds, maxNumberOfVisibleTabs]);

  useEffect(() => {
    setTabLayout((prev) => {
      const next = Array.from(openedApplicationIds).reduce((acc, appId) => {
        const next = acc.clone();
        next.add(appId);
        return next;
      }, prev);

      return Array.from(next.all()).reduce((acc, appId) => {
        if (openedApplicationIds.has(appId)) return acc;

        const next = acc.clone();
        next.remove(appId);
        return next;
      }, next);
    });
  }, [openedApplicationIds]);

  useEffect(() => {
    if (applicationID) {
      setTabLayout((prev) => {
        const next = prev.clone();
        next.addOrShow(applicationID);
        return next;
      });
    }
  }, [applicationID]);

  return tabLayout;
}

export function useTabs(marketCountry: string, maxNumberOfVisibleTabs: number) {
  const { state } = useContext(ApplicationContext);

  const openedApplicationIds = useMemo(
    () => new Set(Object.keys(state.openApplications)),
    [state.openApplications],
  );
  const appIdParam = useParams().applicationID;
  const applicationID =
    appIdParam && openedApplicationIds.has(appIdParam) ? appIdParam : null;
  const tabAppIds = useOverflowGroup(
    openedApplicationIds,
    applicationID,
    maxNumberOfVisibleTabs,
  );

  return useMemo<ITabLayout>(() => {
    return {
      visibleTabs: Array.from(tabAppIds.visible)
        .filter((appId) => appId in state.openApplications)
        .map((appId) => state.openApplications[appId]),
      overflowTabs: Array.from(tabAppIds.overflow)
        .filter((appId) => appId in state.openApplications)
        .map((appId) => state.openApplications[appId]),
    };
  }, [tabAppIds, state.openApplications]);
}
